import { ref, computed } from 'vue'
import { landingPageService } from '@/services/landingPageService'

// State global (singleton)
const state = {
  // Dados
  settings: ref(null),
  customization: ref(null),
  sections: ref(null),
  speakers: ref([]),
  schedules: ref([]),
  sponsors: ref([]),
  testimonials: ref([]),
  faqs: ref([]),
  photos: ref([]),
  videos: ref([]),

  // Estados
  loading: ref(false),
  error: ref(null),
  initialized: ref(false),
  
  // Cache
  lastFetch: ref(null),
  cacheDuration: 5 * 60 * 1000 // 5 minutos
}

export function useLandingPage() {
  /**
   * FASE 1: Carregar dados essenciais (navbar, banner, customização, sections)
   */
  const fetchEssentials = async () => {
    if (state.loading.value) return // Evitar chamadas simultâneas
    
    try {
      state.loading.value = true
      state.error.value = null
      
      console.log('🚀 Carregando dados essenciais...')
      
      // Buscar apenas o necessário para renderizar a página inicial
      const [settingsRes, customizationRes, sectionsRes] = await Promise.all([
        landingPageService.getSettings(),
        landingPageService.getCustomization(),
        landingPageService.getSections()
      ])
      
      state.settings.value = settingsRes
      state.customization.value = customizationRes
      state.sections.value = sectionsRes
      state.initialized.value = true
      state.lastFetch.value = Date.now()

      // Atualiza título da página com nome do evento
      if (settingsRes?.event_name) {
        document.title = settingsRes.event_name
      }

      // Injeta cor do scrollbar como CSS variable
      if (customizationRes?.scrollbar_color) {
        document.documentElement.style.setProperty('--scrollbar-color', customizationRes.scrollbar_color)
      }

      console.log('✅ Dados essenciais carregados!')
      
    } catch (err) {
      console.error('❌ Erro ao carregar dados essenciais:', err)
      state.error.value = err.message || 'Erro ao carregar dados essenciais'
    } finally {
      state.loading.value = false
    }
  }
  
  /**
   * FASE 2: Carregar seções ativas de forma progressiva
   */
  const fetchActiveSections = async () => {
    if (!state.sections.value || state.sections.value.length === 0) return
    
    try {
      console.log('🔄 Carregando seções ativas...')
      
      const activeSectionKeys = state.sections.value
        .filter(s => s.is_active)
        .map(s => s.section_key)
      
      console.log('📋 Seções ativas:', activeSectionKeys)
      
      // Carregar cada seção ativa de forma independente
      const promises = []
      
      if (activeSectionKeys.includes('speakers')) {
        promises.push(
          landingPageService.getSpeakers()
            .then(data => { state.speakers.value = data })
            .catch(err => console.error('Erro ao carregar speakers:', err))
        )
      }
      
      if (activeSectionKeys.includes('schedule')) {
        promises.push(
          landingPageService.getSchedules()
            .then(data => { state.schedules.value = data })
            .catch(err => console.error('Erro ao carregar schedules:', err))
        )
      }
      
      if (activeSectionKeys.includes('sponsors')) {
        promises.push(
          landingPageService.getSponsors()
            .then(data => { state.sponsors.value = data })
            .catch(err => console.error('Erro ao carregar sponsors:', err))
        )
      }
      
      if (activeSectionKeys.includes('testimonials')) {
        promises.push(
          landingPageService.getTestimonials()
            .then(data => { state.testimonials.value = data })
            .catch(err => console.error('Erro ao carregar testimonials:', err))
        )
      }
      
      if (activeSectionKeys.includes('faqs')) {
        promises.push(
          landingPageService.getFaqs()
            .then(data => { state.faqs.value = data })
            .catch(err => console.error('Erro ao carregar faqs:', err))
        )
      }
      
      if (activeSectionKeys.includes('photos')) {
        promises.push(
          landingPageService.getPhotos()
            .then(data => { state.photos.value = data })
            .catch(err => console.error('Erro ao carregar photos:', err))
        )
      }

      if (activeSectionKeys.includes('videos')) {
        promises.push(
          landingPageService.getVideos()
            .then(data => { state.videos.value = data })
            .catch(err => console.error('Erro ao carregar videos:', err))
        )
      }

      // Executar todas as requisições em paralelo
      await Promise.all(promises)
      
      console.log('✅ Seções ativas carregadas!')
      
    } catch (err) {
      console.error('❌ Erro ao carregar seções:', err)
    }
  }
  
  /**
   * Inicialização completa com cache
   */
  const fetchAll = async (forceRefresh = false) => {
    // Verificar cache
    if (!forceRefresh && state.initialized.value && state.lastFetch.value) {
      const timeSinceLastFetch = Date.now() - state.lastFetch.value
      if (timeSinceLastFetch < state.cacheDuration) {
        console.log('✨ Usando dados do cache')
        return
      }
    }
    
    // Evitar múltiplas chamadas simultâneas (LOOP FIX)
    if (state.loading.value) {
      console.warn('⚠️ Requisição já em andamento, ignorando...')
      return
    }
    
    // FASE 1: Carregar essenciais primeiro
    await fetchEssentials()
    
    // FASE 2: Carregar seções ativas progressivamente
    await fetchActiveSections()
    
    // Marcar como inicializado
    state.initialized.value = true
    state.lastFetch.value = Date.now()
  }
  
  /**
   * Seções ativas (computed)
   */
  const activeSections = computed(() => {
    if (state.sections.value === null) return null
    return state.sections.value.filter(s => s.is_active)
  })
  
  /**
   * Verificar se uma seção está ativa
   */
  const isSectionActive = (sectionKey) => {
    if (state.sections.value === null) return true
    return state.sections.value.some(s => s.section_key === sectionKey && s.is_active)
  }
  
  /**
   * Limpar cache
   */
  const clearCache = () => {
    state.initialized.value = false
    state.lastFetch.value = null
  }
  
  /**
   * Reset completo
   */
  const reset = () => {
    state.settings.value = null
    state.customization.value = null
    state.sections.value = null
    state.speakers.value = []
    state.schedules.value = []
    state.sponsors.value = []
    state.testimonials.value = []
    state.faqs.value = []
    state.photos.value = []
    state.videos.value = []
    state.loading.value = false
    state.error.value = null
    state.initialized.value = false
    state.lastFetch.value = null
  }
  
  return {
    // Dados
    settings: computed(() => state.settings.value),
    customization: computed(() => state.customization.value),
    sections: computed(() => state.sections.value),
    speakers: computed(() => state.speakers.value),
    schedules: computed(() => state.schedules.value),
    sponsors: computed(() => state.sponsors.value),
    testimonials: computed(() => state.testimonials.value),
    faqs: computed(() => state.faqs.value),
    photos: computed(() => state.photos.value),
    videos: computed(() => state.videos.value),

    // Estados
    loading: computed(() => state.loading.value),
    error: computed(() => state.error.value),
    initialized: computed(() => state.initialized.value),
    
    // Computeds
    activeSections,
    
    // Métodos
    fetchAll,
    fetchEssentials,
    fetchActiveSections,
    isSectionActive,
    clearCache,
    reset
  }
}